import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import BasicButton from "../../../components/basics/BasicButton";
import { useGetAdminCurrentSeasonQuery, useGetAdminSeasonQuery, useGetAdminSeasonsQuery } from "../api/gameApi";
import { errorText, fmtDay, PHASE_LABEL } from "./adminText";
import { Intro, Label, Notice, PageContainer, Section, SectionTitle, Select, Small, Title } from "./adminStyles";
import SeasonCard from "./SeasonCard";
import SeasonForm from "./SeasonForm";
import LifecyclePanel from "./LifecyclePanel";
import SettlePanel from "./SettlePanel";
import ParticipantsPanel from "./ParticipantsPanel";
import SchedulerPanel from "./SchedulerPanel";

const PHONE_CSS = `
  @media (max-width: 700px) {
    .gadm-page { padding: 12px 8px !important; }
    .gadm-page .gadm-row { gap: 8px !important; }
    .gadm-page label { min-width: 0 !important; flex: 1 1 100% !important; }
    .gadm-page .gadm-table-wrap { margin: 0 -8px; width: calc(100% + 16px); }
    .gadm-nav { flex-wrap: wrap; gap: 8px !important; }
  }
`;

/**
 * /admin/game: run the game season without scripts. The season the page
 * opens on is the newest one that is not archived; ?season=<id> opens another
 * (an archived one, for instance). Every handle on this page comes from the
 * backend through the AOTD emoji gate.
 */
const GameAdminPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("season");
  const list = useGetAdminSeasonsQuery();
  const current = useGetAdminCurrentSeasonQuery(undefined, { skip: !!selectedId });
  const byId = useGetAdminSeasonQuery(selectedId ?? "", { skip: !selectedId });
  const q = selectedId ? byId : current;
  const season = q.data ?? null;
  const seasons = list.data ?? [];
  const past = seasons.filter((s) => s.status === "archived");

  const pick = (id: string) => {
    const next = new URLSearchParams(params);
    if (id) next.set("season", id);
    else next.delete("season");
    setParams(next, { replace: true });
  };

  return (
    <PageContainer className="gadm-page">
      <style>{PHONE_CSS}</style>
      <div className="gadm-nav flex flex-wrap gap-3 items-center" style={{ marginBottom: 16 }}>
        <Link to="/admin">
          <BasicButton text="Admin hub" />
        </Link>
        <Link to="/admin/scheduler">
          <BasicButton text="Scheduler" />
        </Link>
        <Link to="/game">
          <BasicButton text="Open /game" />
        </Link>
      </div>
      <Title>The game</Title>
      <Intro>
        The season on the morning race: its dates, points and special days, who has joined, and the days scored so far.
      </Intro>

      {seasons.length > 1 && (
        <Section aria-label="Pick a season">
          <Label style={{ maxWidth: 420 }}>
            Season
            <Select value={season?.id ?? selectedId ?? ""} onChange={(e) => pick(e.target.value)}>
              {!selectedId && !season && <option value="">none open</option>}
              {seasons.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}, {PHASE_LABEL[s.phase] ?? s.phase}, {s.dayKeyFrom ?? "?"} to {s.dayKeyTo ?? "?"}
                </option>
              ))}
            </Select>
          </Label>
        </Section>
      )}

      {q.isLoading && <Small>Loading the season…</Small>}
      {q.isError && (
        <Notice tone="error" role="alert">
          {errorText(q.error, "Could not load the season.")}
        </Notice>
      )}

      {!q.isLoading && !q.isError && !season && (
        <>
          <Notice tone="warn">There is no season yet. Create it below; nothing shows on /game until you do.</Notice>
          <SeasonForm mode="create" />
          {past.length > 0 && (
            <Section aria-label="Past seasons">
              <SectionTitle>Past seasons</SectionTitle>
              <ul>
                {past.map((s) => (
                  <li key={s.id} style={{ marginBottom: 6 }}>
                    <button type="button" style={{ color: "#663399", textDecoration: "underline" }} onClick={() => pick(s.id)}>
                      {s.name}
                    </button>{" "}
                    <Small as="span">
                      {fmtDay(s.dayKeyFrom)} to {fmtDay(s.dayKeyTo)}
                    </Small>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </>
      )}

      {season && (
        <React.Fragment key={season.id}>
          <SeasonCard season={season} />
          <LifecyclePanel season={season} />
          <SettlePanel season={season} />
          <ParticipantsPanel season={season} />
          <SchedulerPanel season={season} />
          {season.phase !== "archived" && <SeasonForm mode="edit" season={season} />}
        </React.Fragment>
      )}
    </PageContainer>
  );
};

export default GameAdminPage;
